import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle, InfoCircle } from "@untitledui/icons";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import ModalBase from "@/components/ui/modal/modal-base";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/providers/toast-provider";
import { GroupMultiSelect } from "@/shared/components/group-multi-select";
import { useGroupsQuery } from "@/shared/hooks";
import formartDocumet from "@/utils/formats";
import { UserMutationError } from "../errors/user-mutation.errors";
import { useCreateUserMutation } from "../hooks";
import { newUserSchema } from "../schemas";
import type { FormValuesNewUser, NewUserProps } from "../types";

const getRoleOptions = (roles: string[]) => {
	const userRoles = roles.map((r) => r.toLowerCase());
	const isAdmin = userRoles.includes("admin");
	const isManager = userRoles.includes("manager");
	const isTelemetryAdmin = userRoles.includes("telemetryadmin");

	const options: { id: string; label: string }[] = [];

	if (isAdmin || isManager) {
		options.push(
			{ id: "Driver", label: "Motorista" },
			{ id: "Manager", label: "Gestor de frota" }
		);
	}

	if (isAdmin) {
		options.push({ id: "Admin", label: "Administrador" });
	}

	if (isAdmin) {
		options.push(
			{ id: "TelemetryAdmin", label: "Telemetria Admin" },
			{ id: "TelemetryMember", label: "Telemetria Membro" }
		);
	} else if (isTelemetryAdmin) {
		options.push({ id: "TelemetryMember", label: "Telemetria Membro" });
	}

	return options;
};

export const NewUser = ({ isOpen, onClose, onSuccess }: NewUserProps) => {
	const { mutateAsync, isPending } = useCreateUserMutation();
	const { user } = useAuth();
	const { data: groups = [], isLoading: isGroupsLoading } = useGroupsQuery({
		enabled: isOpen,
	});

	const [success, setSuccess] = useState(false);
	const { showToast } = useToast();
	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		watch,
		trigger,
	} = useForm<FormValuesNewUser>({
		resolver: zodResolver(newUserSchema),
		defaultValues: {
			name: "",
			cpf: "",
			email: "",
			role: "",
			group_ids: [],
		},
	});

	const roleOptions = getRoleOptions(user?.role ?? []);

	const onSubmit = async (data: FormValuesNewUser) => {
		const userRoles = (user?.role ?? []).map((r) => r.toLowerCase());
		const isAdmin = userRoles.includes("admin");
		const isTelemetryAdmin = userRoles.includes("telemetryadmin");

		if (isTelemetryAdmin && !isAdmin && data.role !== "TelemetryMember") {
			showToast({
				title: "Perfil não permitido",
				variant: "error",
				description:
					"Você só pode cadastrar usuários com perfil Telemetria Membro.",
			});
			return;
		}

		try {
			await mutateAsync({
				name: data.name,
				email: data.email,
				cpf: data.cpf.replace(/\D/g, ""),
				role: data.role,
				groupIds: data.group_ids,
			});

			showToast({
				title: "Usuário criado",
				variant: "success",
				description: "O usuário foi cadastrado com sucesso.",
			});

			setSuccess(true);
			onSuccess?.();
		} catch (err) {
			if (
				err instanceof Error &&
				err.message === UserMutationError.USER_ALREADY_EXISTS
			) {
				showToast({
					title: "Usuário já existe",
					variant: "error",
					description: "Usuário com e-mail ou CPF já existente.",
				});

				setValue("cpf", "");
				setValue("email", "");
				return;
			}

			showToast({
				title: "Erro ao criar",
				variant: "error",
				description: "Não foi possível cadastrar o usuário. Tente novamente.",
			});
		}
	};

	const handleClose = () => {
		reset();
		setSuccess(false);
		onClose();
	};

	return (
		<ModalBase
			actions={
				success
					? [{ label: "Concluir", onClick: handleClose, color: "primary" }]
					: [
							{ label: "Cancelar", onClick: handleClose, color: "secondary" },
							{
								label: "Salvar",
								onClick: handleSubmit(onSubmit),
								color: "primary",
								disabled: isPending,
							},
						]
			}
			description={
				success
					? ""
					: "Preencha todos os campos abaixo para cadastrar um novo usuário"
			}
			height="auto"
			icon={false}
			isOpen={isOpen}
			onClose={handleClose}
			title={success ? "" : "Cadastrar novo usuário"}
			width={494}
		>
			{success ? (
				<div className="m-auto flex w-[374px] flex-col items-center gap-5 p-10 text-center">
					<FeaturedIcon color="success" icon={<CheckCircle />} size="md" />
					<h1 className="font-semibold text-[#181D27] text-[32px] leading-[100%]">
						Usuário cadastrado com sucesso!
					</h1>
					<p className="w-[374px] text-[20px]">
						As informações de acesso foram enviadas para o{" "}
						<b>e-mail cadastrado</b>.
					</p>
				</div>
			) : (
				<div className="mb-8 flex flex-col gap-5 px-0.5">
					<div className="relative">
						<Input
							hint={errors.name?.message}
							isInvalid={!!errors.name}
							label="Nome"
							maxLength={50}
							onBlur={() => trigger("name")}
							onChange={(value) => {
								const sanitized = value
									.replace(/^\s+/g, "")
									.replace(/\s{2,}/g, " ");

								setValue("name", sanitized);
							}}
							placeholder="Nome completo"
							value={watch("name")}
						/>
						{errors.name?.message ? (
							<span className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 text-red-500">
								<InfoCircle size={16} />
							</span>
						) : null}
					</div>

					<div className="relative">
						<Input
							hint={errors.cpf?.message}
							isInvalid={!!errors.cpf}
							label="CPF"
							onBlur={() => trigger("cpf")}
							onChange={(value) => {
								if (value.length <= 14) {
									setValue("cpf", formartDocumet.formartCPF(value));
								}
							}}
							placeholder="000.000.000-00"
							value={watch("cpf")}
						/>
						{errors.cpf?.message ? (
							<span className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 text-red-500">
								<InfoCircle size={16} />
							</span>
						) : null}
					</div>

					<div className="relative">
						<Input
							hint={errors.email?.message}
							isInvalid={!!errors.email}
							label="E-mail"
							maxLength={254}
							onBlur={() => {
								const value = watch("email")?.trim();
								setValue("email", value);
								trigger("email");
							}}
							onChange={(value) => {
								const sanitized = value.replace(/\s+/g, "").toLowerCase();

								setValue("email", sanitized);
							}}
							placeholder="E-mail válido para acesso"
							value={watch("email")}
						/>
						{errors.email ? (
							<span className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 text-red-500">
								<InfoCircle size={16} />
							</span>
						) : null}
					</div>

					<div className="relative">
						<Select
							defaultSelectedKey={watch("role")}
							hint={errors.role?.message}
							isInvalid={!!errors.role}
							items={roleOptions}
							label="Tipo de usuário"
							placeholder="Selecione um perfil"
						>
							{(item) => (
								<SelectItem
									id={item.id}
									label={item.label}
									onClick={() => setValue("role", String(item.id))}
								/>
							)}
						</Select>
					</div>

					<div className="relative">
						<Controller
							control={control}
							name="group_ids"
							render={({ field }) => (
								<GroupMultiSelect
									groups={groups}
									hint={errors.group_ids?.message}
									isInvalid={!!errors.group_ids}
									isLoading={isGroupsLoading}
									label="Grupo"
									onChange={field.onChange}
									value={field.value ?? []}
								/>
							)}
						/>
					</div>
				</div>
			)}
		</ModalBase>
	);
};
